import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { formatDate } from "./overrideApprovalHelpers";

const getEmployeeName = (employees, employeeId, t) => {
  const emp = employees.find((e) => e.id === employeeId);
  return emp ? `${emp.last_name} ${emp.first_name}` : t("planning.overridesPanel.unknownEmployee");
};

const getScheduleName = (schedules, scheduleId, t) => {
  const schedule = schedules.find((s) => s.id === scheduleId);
  return schedule?.name || t("planning.overridesPanel.unknownSchedule");
};

const ApprovalDialog = ({
  open, onOpenChange,
  approvalAction, selectedOverride,
  employees, schedules,
  rejectionReason, setRejectionReason,
  handleApproveOrReject,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
          {approvalAction === "approve" ? (
            <span className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              {t("planning.approvalsPanel.approveTitle")}
            </span>
          ) : (
            <span className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              {t("planning.approvalsPanel.rejectTitle")}
            </span>
          )}
        </DialogTitle>
        <DialogDescription>
          {selectedOverride && (
            <>
              {t("planning.approvalsPanel.requestDescription", {
                employee: getEmployeeName(employees, selectedOverride.employee_id, t),
                date: formatDate(selectedOverride.date, i18n.language || "fr"),
              })}
            </>
          )}
        </DialogDescription>
      </DialogHeader>

      {selectedOverride && (
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("planning.overridesPanel.requestedScheduleLabel")}</span>
              <span className="font-medium">
                {getScheduleName(schedules, selectedOverride.override_schedule_id, t)}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">{t("planning.overridesPanel.reasonLabel")}</span>
              <p className="mt-1 p-2 bg-muted/50 rounded">{selectedOverride.reason}</p>
            </div>
          </div>

          {approvalAction === "reject" && (
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">{t("planning.approvalsPanel.rejectionReason")}</Label>
              <Textarea
                id="rejection-reason"
                placeholder={t("planning.approvalsPanel.rejectionPlaceholder")}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="border-red-900/40 focus:border-red-500"
              />
              <p className="text-xs text-muted-foreground">
                {t("planning.approvalsPanel.rejectionHelp")}
              </p>
            </div>
          )}

          {approvalAction === "approve" && (
            <div className="flex items-start gap-2 p-3 bg-green-950/20 border border-green-900/30 rounded">
              <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-foreground">{t("planning.approvalsPanel.approvalConfirmation")}</p>
                <p className="text-muted-foreground mt-1">
                  {t("planning.approvalsPanel.approvalConfirmationText")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button
          onClick={handleApproveOrReject}
          className={approvalAction === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
        >
          {approvalAction === "approve" ? (
            <><CheckCircle className="h-4 w-4 mr-2" />{t("planning.approvalsPanel.approve")}</>
          ) : (
            <><XCircle className="h-4 w-4 mr-2" />{t("planning.approvalsPanel.reject")}</>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default ApprovalDialog;
